import React, {Fragment, useEffect} from 'react'

import slideUp from '../../utilities/slideUp';
import slideDown from '../../utilities/slideDown';
import getParents from '../../utilities/getParents';

import { NavLink, useLocation } from "react-router-dom";
import {FaWarehouse} from "react-icons/fa";
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import {
    BanknotesIcon,
    FolderIcon,
    Squares2X2Icon,
    UserGroupIcon,
    WrenchScrewdriverIcon,
    ClipboardDocumentListIcon,
} from "@heroicons/react/24/solid";
import {TbMessageChatbotFilled} from "react-icons/tb";
import {PiChartDonutFill} from "react-icons/pi";
import {HiDocumentDuplicate} from "react-icons/hi2";
import {FaHeadset} from "react-icons/fa6";
import { useTranslation } from 'react-i18next';

const settingsItem = (t) => ({
    icon: <Cog6ToothIcon className="h-6 w-6" />,
    text: t('nav.settings'),
    link: "/settings",
});

function buildAdminMenu(t) {
    return [
        { heading: t('nav.main') },
        { icon: <Squares2X2Icon className="h-6 w-6" />, text: t('nav.home'), link: "/" },
        { icon: <UserGroupIcon className="h-6 w-6" />, text: t('nav.patients'), link: "/patients" },
        { icon: <FaHeadset className="h-6 w-6" />, text: t('nav.callCenter'), link: "/call-center" },
        { icon: <TbMessageChatbotFilled />, text: t('nav.sms'), link: "/sms" },
        { heading: t('nav.catalog') },
        {
            icon: <FolderIcon className="h-6 w-6" />, text: t('nav.catalog'), link: "#",
            sub: [
                { text: t('nav.users'), link: "/users" },
                { text: t('nav.doctors'), link: "/catalog/doctors" },
                { text: t('nav.discounts'), link: "/catalog/discount" },
                { text: t('nav.smsTemplates'), link: "/catalog/sms-templates" },
                { text: t('nav.services'), link: "/catalog/services" },
                { text: t('nav.epidem'), link: "/catalog/epidemiological_history" },
                { text: t('nav.mkb10'), link: "/catalog/international-classification-of-diseases" },
                { text: t('nav.partners'), link: "/catalog/partners" }
            ]
        },
        { heading: t('nav.finance') },
        {
            icon: <BanknotesIcon className="h-6 w-6" />, text: t('nav.cash'), link: "#",
            sub: [
                { text: t('nav.debtors'), link: "/payments/debtors", blank: "true" },
                { text: t('nav.payments'), link: "/payments/history", blank: "true" },
            ]
        },
        { heading: t('nav.appointments') },
        {
            icon: <HiDocumentDuplicate className="h-6 w-6" />, text: t('nav.allAppointments'), link: "#",
            sub: [
                { text: t('nav.queue'), link: "/appointments/queue" },
                { text: t('nav.reappointments'), link: "/appointments/reappointments" },
                { text: t('nav.followups'), link: "/appointments/followups" },
                { text: t('nav.recall'), link: "/appointments/recall-calls" },
                { text: t('nav.patientActivity'), link: "/patients/activity" },
            ]
        },
        { heading: t('nav.analytics') },
        {
            icon: <PiChartDonutFill />, text: t('nav.reports'), link: "#",
            sub: [
                { text: t('nav.reportsPatients'), link: "/reports/patients" },
                { text: t('nav.reportsDoctors'), link: "/reports/doctors" },
                { text: t('nav.reportsFinance'), link: "/reports/finance" },
                { text: t('nav.reportsPartners'), link: "/reports/partners" },
            ]
        },
        { heading: t('nav.inventory') },
        {
            icon: <FaWarehouse  />, text: t('nav.storeProducts'), link: "#",
            sub: [
                { text: t('nav.categories'), link: "/store/category" },
                { text: t('nav.warehouse'), link: "/store/warehouse" },
                { text: t('nav.requests'), link: "/store/request" },
                { text: t('nav.resendRequests'), link: "/store/resend-requests" },
            ]
        },
        { heading: t('nav.technicianLab') },
        { icon: <WrenchScrewdriverIcon className="h-6 w-6" />, text: t('nav.technicianObjects'), link: "/catalog/technician-objects" },
        { icon: <ClipboardDocumentListIcon className="h-6 w-6" />, text: t('nav.technicianRequests'), link: "/admin/technician-requests" },
        settingsItem(t),
    ];
}

function buildFinancierMenu(t) {
    return [
        { heading: t('nav.main') },
        { icon: <Squares2X2Icon className="h-6 w-6" />, text: t('nav.home'), link: "/" },
        { heading: t('nav.finance') },
        {
            icon: <BanknotesIcon className="h-6 w-6" />, text: t('nav.cash'), link: "#",
            sub: [
                { text: t('nav.debtors'), link: "/payments/debtors", blank: "true" },
                { text: t('nav.payments'), link: "/payments/history", blank: "true" },
            ]
        },
        { heading: t('nav.inventory') },
        {
            icon: <FaWarehouse  />, text: t('nav.storeProducts'), link: "#",
            sub: [
                { text: t('nav.categories'), link: "/store/category" },
                { text: t('nav.warehouse'), link: "/store/warehouse" },
                { text: t('nav.requests'), link: "/store/request" },
                { text: t('nav.resendRequests'), link: "/store/resend-requests" },
            ]
        },
        settingsItem(t),
    ];
}

function buildReceptionMenu(t) {
    return [
        { heading: t('nav.main') },
        { icon: <Squares2X2Icon className="h-6 w-6" />, text: t('nav.home'), link: "/" },
        { icon: <UserGroupIcon className="h-6 w-6" />, text: t('nav.patients'), link: "/patients" },
        { icon: <TbMessageChatbotFilled />, text: t('nav.sms'), link: "/sms" },
        { heading: t('nav.finance') },
        {
            icon: <BanknotesIcon className="h-6 w-6" />, text: t('nav.cash'), link: "#",
            sub: [
                { text: t('nav.debtors'), link: "/payments/debtors", blank: "true" },
                { text: t('nav.payments'), link: "/payments/history", blank: "true" },
            ]
        },
        { heading: t('nav.appointments') },
        {
            icon: <HiDocumentDuplicate className="h-6 w-6" />, text: t('nav.allAppointments'), link: "#",
            sub: [
                { text: t('nav.queue'), link: "/appointments/queue" },
                { text: t('nav.reappointments'), link: "/appointments/reappointments" },
                { text: t('nav.followups'), link: "/appointments/followups" },
                { text: t('nav.recall'), link: "/appointments/recall-calls" },
                { text: t('nav.patientActivity'), link: "/patients/activity" },
            ]
        },
        settingsItem(t),
    ];
}

function buildDoctorMenu(t) {
    return [
        { heading: t('nav.main') },
        { icon: <Squares2X2Icon className="h-6 w-6" />, text: t('nav.home'), link: "/" },
        { icon: <UserGroupIcon className="h-6 w-6" />, text: t('nav.doctorVisits'), link: "/doctor/visits" },
        { icon: <UserGroupIcon className="h-6 w-6" />, text: t('nav.doctorRequest'), link: "/doctor/request" },
        { icon: <ClipboardDocumentListIcon className="h-6 w-6" />, text: t('nav.technicianRequests'), link: "/doctor/technician-requests" },
        settingsItem(t),
    ];
}

function buildTechnicianMenu(t) {
    return [
        { heading: t('nav.main') },
        { icon: <ClipboardDocumentListIcon className="h-6 w-6" />, text: t('nav.technicianRequests'), link: "/technician/requests" },
        settingsItem(t),
    ];
}

const Menu = ({setSidebarVisibility}) => {
    const location = useLocation();
    const { t } = useTranslation();
    const userRole = localStorage.getItem("userRole");

    const getMenuData = () => {
        switch (userRole) {
            case 'financier':
                return buildFinancierMenu(t);
            case 'reception':
                return buildReceptionMenu(t);
            case 'doctor':
                return buildDoctorMenu(t);
            case 'technician':
                return buildTechnicianMenu(t);
            default:
                return buildAdminMenu(t);
        }
    };

    const menuDataToRender = getMenuData();


    // variables for Sidebar
    let menu = {
        classes: {
            main: 'nk-menu',
            item:'nk-menu-item',
            link:'nk-menu-link',
            toggle: 'nk-menu-toggle',
            sub: 'nk-menu-sub',
            subparent: 'has-sub',
            active: 'active',
            current: 'current-page'
        },
    };

    let currentLink = function(selector){
        let elm = document.querySelectorAll(selector);
        elm.forEach(function(item){
            var activeRouterLink = item.classList.contains('active');
            if (activeRouterLink) {
                let parents = getParents(item,`.${menu.classes.main}`, menu.classes.item);
                parents.forEach(parentElemets =>{
                    parentElemets.classList.add(menu.classes.active, menu.classes.current);
                    let subItem = parentElemets.querySelector(`.${menu.classes.sub}`);
                    subItem !== null && (subItem.style.display = "block")
                })
                
            } else {
                item.parentElement.classList.remove(menu.classes.active, menu.classes.current);
            }
        })
    } 
    // dropdown toggle
    let dropdownToggle = function(elm){
        let parent = elm.parentElement;
        let nextelm = elm.nextElementSibling;
        let speed = nextelm.children.length > 5 ? 400 + nextelm.children.length * 10 : 400;
        if(!parent.classList.contains(menu.classes.active)){
            parent.classList.add(menu.classes.active);
            slideDown(nextelm,speed);
        }else{
            parent.classList.remove(menu.classes.active);
            slideUp(nextelm,speed);
        }
    }

    // dropdown close siblings
    let closeSiblings = function(elm){
        let parent = elm.parentElement;
        let siblings = parent.parentElement.children;
        Array.from(siblings).forEach(item => {
        if(item !== parent){
            item.classList.remove(menu.classes.active);
            if(item.classList.contains(menu.classes.subparent)){
            let subitem = item.querySelectorAll(`.${menu.classes.sub}`);
            subitem.forEach(child => {
                child.parentElement.classList.remove(menu.classes.active);
                slideUp(child,400);
            })
            }
        }
        });
    }

    let menuToggle = function(e){
        e.preventDefault();
        let item = e.target.closest(`.${menu.classes.toggle}`)
        dropdownToggle(item);
        closeSiblings(item);
    }

    let routeChange = function(e){
        let selector = document.querySelectorAll(".nk-menu-link")
        selector.forEach((item, index)=>{
            if(item.classList.contains('active')){
                closeSiblings(item);
                item.parentElement.classList.add("active");
            }else{
                item.parentElement.classList.remove("active");
                currentLink(`.${menu.classes.link}`);
            }
        })
    }
    
    useEffect(() =>{
        routeChange();
    },[location.pathname])

    useEffect(() =>{
        currentLink(`.${menu.classes.link}`);
        // eslint-disable-next-line
    },[null])

  return (
    <ul className="nk-menu">
        {menuDataToRender.map((item,index) =>
            <Fragment key={index}>
                {!item.heading ?
                    (
                        !item.sub ?
                        <li className={`nk-menu-item py-px px-1 group/item group-[&.is-compact:not(.has-hover)]/sidebar:px-0 ${item.sub ? 'has-sub' : ''}`} >
                            <NavLink onClick={()=> setSidebarVisibility(false)} target={item.blank && "_blank"} className="nk-menu-link nk-route-toggle flex relative items-center gap-2.5 mx-0 rounded-lg py-2 ps-2.5 pe-7 text-[13px] font-medium group transition-colors hover:bg-white/[0.08] [&.active]:bg-white/[0.12] [&.active]:text-white group-[&.is-compact:not(.has-hover)]/sidebar:justify-center group-[&.is-compact:not(.has-hover)]/sidebar:px-2 group-[&.is-compact:not(.has-hover)]/sidebar:pe-2" to={item.link} end>
                                {item.icon && <span className="w-8 h-8 inline-flex items-center justify-center shrink-0 text-white/85 group-[.active]/item:text-white [&_svg]:w-5 [&_svg]:h-5">
                                     {item.icon && <span className="leading-none icon">{item.icon}</span>}
                                </span>}
                                {item.text && <span className="group-[&.is-compact:not(.has-hover)]/sidebar:hidden flex-grow inline-block whitespace-nowrap text-white/80 group-[.active]/item:text-white group-hover:text-white">{item.text}</span>}
                            </NavLink>
                        </li>
                        :
                        <li className={`nk-menu-item py-px px-1 group/item group-[&.is-compact:not(.has-hover)]/sidebar:px-0 ${item.sub ? 'has-sub' : ''}`} >
                            <a className="nk-menu-link nk-menu-toggle flex relative items-center gap-2.5 mx-0 rounded-lg py-2 ps-2.5 pe-7 text-[13px] font-medium group transition-colors hover:bg-white/[0.08] group-[&.is-compact:not(.has-hover)]/sidebar:justify-center group-[&.is-compact:not(.has-hover)]/sidebar:px-2 group-[&.is-compact:not(.has-hover)]/sidebar:pe-2" onClick={menuToggle} href="#expand">
                                {item.icon && <span className="w-8 h-8 inline-flex items-center justify-center shrink-0 text-white/85 group-[.active]/item:text-white [&_svg]:w-5 [&_svg]:h-5">
                                     {item.icon && <span className="leading-none icon">{item.icon}</span>}
                                </span>}
                                {item.text && <span className="group-[&.is-compact:not(.has-hover)]/sidebar:hidden flex-grow inline-block whitespace-nowrap text-white/80 group-[.active]/item:text-white group-hover:text-white">{item.text}</span>}
                                <em className="group-[&.is-compact:not(.has-hover)]/sidebar:hidden text-xs leading-none text-white/40 group-[.active]/item:text-white/80 absolute end-3 top-1/2 -translate-y-1/2 group-[.active]/item:rotate-90 transition-transform duration-200 icon ni ni-chevron-right"></em>
                            </a>
                            <ul className="nk-menu-sub mb-0.5 hidden group-[&.is-compact:not(.has-hover)]/sidebar:!hidden">
                                {item.sub.map((itemsub1,index1) =>
                                    <Fragment key={index1}>
                                        {!itemsub1.sub ?
                                            <li className={`nk-menu-item py-px group/sub1`} >
                                                <NavLink  onClick={()=> setSidebarVisibility(false)}  to={itemsub1.link} className="nk-menu-link nk-route-toggle flex relative items-center rounded-md py-1.5 pe-3 ps-11 me-1 text-[12px] font-medium text-white/70 hover:text-white hover:bg-white/[0.06] [&.active]:text-white [&.active]:bg-white/[0.1] transition-colors" end>
                                                    <span className="whitespace-nowrap flex-grow inline-block">{itemsub1.text}</span>
                                                </NavLink>
                                            </li>
                                            :
                                            <li className={`nk-menu-item py-px group/sub1 ${itemsub1.sub ? 'has-sub' : ''}`}>
                                                <a className="nk-menu-link nk-menu-toggle flex relative items-center align-middle py-1.5 pe-10 ps-[calc(theme(spacing.6)+theme(spacing.9))] font-normal leading-5 text-sm tracking-normal normal-case" onClick={menuToggle} href="#expand">
                                                    <span className="text-white/75 group-[.active]/sub1:text-white hover:text-white whitespace-nowrap flex-grow inline-block">{itemsub1.text}</span>
                                                    <em className="text-base leading-none text-slate-400 group-[.active]/sub1:text-primary-500 absolute end-5 top-1/2 -translate-y-1/2 rtl:-scale-x-100 group-[.active]/sub1:rotate-90 group-[.active]/sub1:rtl:-rotate-90 transition-all duration-300 icon ni ni-chevron-right"></em>
                                                </a>
                                                <ul className="nk-menu-sub hidden ms-[calc(theme(spacing.6)+theme(spacing.9))] border-s border-white/15 my-2">
                                                    {itemsub1.sub.map((itemsub2,index2) =>
                                                        <Fragment key={index2}>
                                                            <li className={`nk-menu-item py-px group/sub2`}>
                                                                <NavLink onClick={()=> setSidebarVisibility(false)} to={itemsub2.link} className="nk-menu-link nk-route-toggle flex relative items-center align-middle py-1.5 pe-10 ps-4 font-normal leading-5 text-sm tracking-normal normal-case" end>
                                                                    <span className="text-white/75 group-[.active]/sub2:text-white hover:text-white whitespace-nowrap flex-grow inline-block">{itemsub2.text}</span>
                                                                </NavLink>
                                                            </li>
                                                        </Fragment>
                                                    )}
                                                </ul>
                                            </li>}
                                    </Fragment>
                                )}
                            </ul>
                        </li>
                    )
                :
                <li className="relative first:pt-1 pt-5 pb-1 px-3 first:before:hidden before:absolute before:h-px before:w-[calc(100%-1.5rem)] before:start-3 before:top-2.5 before:bg-white/10 group-[&.is-compact:not(.has-hover)]/sidebar:hidden group-[&.is-compact:not(.has-hover)]/sidebar:py-0 group-[&.is-compact:not(.has-hover)]/sidebar:pt-1" >
                    <h6 className="text-[10px] text-white/40 whitespace-nowrap uppercase font-semibold tracking-wide">{item.heading}</h6>
                </li>}
            </Fragment>
        )}
    </ul>
  )
}

export default Menu